/**
 * AuthenticatedComponent
 *
 * Exports a higher order component that requires the visitor to be authenticated.
 *
 * Parameters:
 * Component - required protected component
 * objectType - optional string: object type to be protected
 * permissionType - optional string: write, create or read
 * objectId - optional boolean: set to true if access check should be for specific id from parameter id
 *
 * Example:
 * AuthenticatedComponent(ComponentToBeAuthenticated, 'user', 'write', true)
 */

import React from 'react';
import { connect } from 'react-redux';
import idx from 'idx';

import { markActivity, resetActivity } from '../actions/activity';
import { getLoggedInUser } from '../actions/auth';
import ErrorMessage from '../Error';
import Sidebar from 'components/Sidebar';
import TopBar from 'components/TopBar';

export default (Component, rules = [], objectId = false) => {
	class AuthenticatedComponent extends React.Component {
		state = {
			hasError: false,
		};

		componentDidMount() {
			this.authenticatedCheck();
			this.getUserIfNotInStore();
			this.authorizationCheck();
		}

		componentDidCatch() {
			this.setState({
				hasError: true,
			});
		}

		authenticatedCheck() {
			const isAuthenticated = idx(this.props, _ => _.auth.isAuthenticated);

			if (!isAuthenticated) {
				const redirectUrl =
					idx(this.props, _ => _.history.location.pathname) || '/';

				this.props.history.replace(`/login?url=${redirectUrl}`);
			}
		}

		getUserIfNotInStore() {
			const { entities, auth, getLoggedInUser } = this.props;
			const { user, isAuthenticated } = auth;

			if (!user || !isAuthenticated) return;

			if (entities.users[user]) return;

			getLoggedInUser();
		}

		authorizationCheck() {
			const { can, user } = this.props.auth;

			if (!rules || !rules.length) return;

			const isAllowed = rules.reduce((allowed, ruleSet) => {
				const objectType = ruleSet[0];
				const permissionType = ruleSet[1];

				// If ruleset is result of computed property (boolean)
				// if true - set allowed to true, else return the current allowed value
				if (typeof ruleSet === 'boolean') {
					if (ruleSet) return (allowed = true);

					return allowed;
				}

				// Read / write for object type, but not a specified object id
				if (objectType && permissionType && !objectId) {
					// Check if user has read / write access for whole object type
					const hasAuthorization = idx(
						can,
						_ => _[permissionType].types[objectType]
					);

					if (!hasAuthorization) {
						return allowed;
					}
				}

				// Read / write for object type with specified object id
				if (objectType && permissionType && objectId) {
					const { id } = this.props.match.params;

					// Get all object types in the auth can for permission type
					const permissionObjects =
						idx(can, _ => _[permissionType].objects) || [];

					// All ids for object type where read / write is correct
					const idsOfAuthorizedByObjectType = permissionObjects
						.filter(obj => obj.type === objectType)
						.map(obj => obj.id);

					// Return if id is in authorized ids array
					const idInAuthorizationsList = id =>
						idsOfAuthorizedByObjectType.indexOf(id) !== -1;

					// Authorization for read / write for whole object type
					const hasAuthorizationForType =
						idx(can, _ => _[permissionType].types[objectType]) || false;

					// Check if read / write access for object type with id
					const hasAuthorizationForId = idInAuthorizationsList(Number(id));

					// Checck if trying to access users profile page
					const isOwnProfilePage = objectType === 'user' && Number(id) === user;
					if (
						!hasAuthorizationForType &&
						!hasAuthorizationForId &&
						!isOwnProfilePage
					) {
						return allowed;
					}
				}

				return (allowed = true);
			}, false);

			if (!isAllowed) {
				if (this.props.history.location.pathname === '/login') return;

				return this.props.history.replace('/');
			}
		}

		render() {
			return (
				<div>
					<TopBar />
					<div style={{ display: 'flex' }}>
						<Sidebar />
						<div style={{ flex: 1 }}>
							{this.state.hasError ? (
								<ErrorMessage withSidebar />
							) : (
								<Component {...this.props} />
							)}
						</div>
					</div>
				</div>
			);
		}
	}

	const mapStateToProps = state => ({
		auth: state.auth,
		activity: state.activity,
		entities: state.entities,
	});

	const mapDispatchToProps = dispatch => ({
		markActivity: type => dispatch(markActivity(type)),
		resetActivity: () => dispatch(resetActivity()),
		getLoggedInUser: () => dispatch(getLoggedInUser()),
	});

	return connect(mapStateToProps, mapDispatchToProps)(AuthenticatedComponent);
};



// WEBPACK FOOTER //
// ./src/hoc/AuthenticatedComponent.js